import React, { useState, useEffect } from 'react';
import { Routes, Route, Navigate, useNavigate, useLocation } from 'react-router-dom';
import { Layout, Menu, Button, Typography, Space, Avatar, Dropdown } from 'antd';
import { 
  UserOutlined, 
  LogoutOutlined, 
  DashboardOutlined,
  SettingOutlined,
  FileTextOutlined,
  MenuOutlined
} from '@ant-design/icons';
import { useAuth } from '../contexts/AuthContext';
import DataTable from './DataTable';
import TabA from './TabA';
import TaskTab from './TaskTab';
import TeamManagement from './TeamManagement';
import CategoryTab from './CategoryTab';

const { Header, Content } = Layout;
const { Title, Text } = Typography;

const Dashboard: React.FC = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [selectedTab, setSelectedTab] = useState('A');
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 767);
  const [menuCollapsed, setMenuCollapsed] = useState(false);

  // Get available tabs based on user role
  const getAvailableTabs = () => {
    if (!user) return [];
    
    switch (user.role) {
      case 'Admin':
        return [
          { key: 'A', label: 'User Management', path: '/dashboard/tab-a' },
          { key: 'TEAM', label: 'Team Management', path: '/dashboard/team' },
          { key: 'TASK', label: 'Task Management', path: '/dashboard/task' },
          { key: 'CATEGORY', label: 'Category Management', path: '/dashboard/category' },
          // { key: 'B', label: 'Tab B (Admin & Leader)', path: '/dashboard/tab-b' },
          // { key: 'C', label: 'Tab C (All Users)', path: '/dashboard/tab-c' },
        ];
      case 'Leader':
        return [
          { key: 'TASK', label: 'Task Management', path: '/dashboard/task' },
          { key: 'CATEGORY', label: 'Category Management', path: '/dashboard/category' },
          { key: 'A', label: 'User Management', path: '/dashboard/tab-a' },
        ];
      case 'Employee':
        return [
          { key: 'TASK', label: 'Task Management', path: '/dashboard/task' },
        ];
      default:
        return [];
    }
  };

  const availableTabs = getAvailableTabs();

  // Handle window resize for responsive
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 767);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Set initial tab based on user role and current location
  useEffect(() => {
    if (availableTabs.length > 0) {
      const currentTab = location.pathname.split('/').pop()?.replace('tab-', '').toUpperCase();
      if (currentTab && availableTabs.some(tab => tab.key === currentTab)) {
        setSelectedTab(currentTab);
      } else {
        // Navigate to first available tab
        navigate(availableTabs[0].path);
        setSelectedTab(availableTabs[0].key);
      }
    }
  }, [user, location, navigate, availableTabs]);

  /**
   * Handle tab selection
   * @param key - Selected tab key
   */
  const handleTabSelect = (key: string) => {
    const tab = availableTabs.find(t => t.key === key);
    if (tab) {
      setSelectedTab(key);
      navigate(tab.path);
    }
  };

  /**
   * Handle logout
   */
  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  // User dropdown menu items
  const userMenuItems = [
    {
      key: 'profile',
      icon: <UserOutlined />,
      label: 'Profile',
      onClick: () => console.log('Profile clicked'),
    },
    {
      key: 'settings',
      icon: <SettingOutlined />,
      label: 'Settings',
      onClick: () => console.log('Settings clicked'),
    },
    {
      type: 'divider' as const,
    },
    {
      key: 'logout',
      icon: <LogoutOutlined />,
      label: 'Logout',
      onClick: handleLogout,
    },
  ];

  return (
    <Layout className="main-layout">
      {/* Header */}
      <Header className="header">
        <div style={{ display: 'flex', alignItems: 'center', flex: 1, minWidth: 0 }}>
          <DashboardOutlined style={{ fontSize: isMobile ? '20px' : '24px', marginRight: isMobile ? '8px' : '12px', color: '#1890ff', flexShrink: 0 }} />
          <Title level={isMobile ? 5 : 4} style={{ margin: 0, color: '#1890ff', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {isMobile ? 'GPhotoPlus' : 'GPhotoPlus Management'}
          </Title>
        </div>

        <Space size={isMobile ? 'small' : 'middle'} style={{ flexShrink: 0 }}>
          {!isMobile && (
            <Text style={{ color: '#666', fontSize: '14px' }}>
              Welcome, {user?.username} ({user?.role})
              {user?.team && <span style={{ color: '#1890ff' }}> • Team: {user.team}</span>}
            </Text>
          )}
          <Dropdown
            menu={{ items: userMenuItems }}
            placement="bottomRight"
            arrow
          >
            <Button 
              type="text" 
              icon={<Avatar icon={<UserOutlined />} size={isMobile ? 'small' : 'default'} />}
              style={{ padding: isMobile ? '4px' : '8px' }}
            />
          </Dropdown>
        </Space>
      </Header>

      {/* Content */}
      <Content className="content">
        {/* Navigation Tabs */}
        <Menu
          mode={isMobile ? 'vertical' : 'horizontal'}
          selectedKeys={[selectedTab]}
          onSelect={({ key }) => handleTabSelect(key)}
          style={{ 
            marginBottom: isMobile ? '16px' : '24px',
            borderBottom: isMobile ? 'none' : '1px solid #f0f0f0'
          }}
          className={isMobile ? 'mobile-menu' : ''}
        >
          {availableTabs.map(tab => (
            <Menu.Item key={tab.key} style={{ fontSize: isMobile ? '14px' : '16px' }}>
              {tab.label}
            </Menu.Item>
          ))}
        </Menu>

        {/* Tab Content */}
        <div className="tab-content">
          <Routes>
            <Route 
              path="tab-a" 
              element={
                user?.role === 'Admin' || user?.role === 'Leader' ? (
                  <TabA />
                ) : (
                  <Navigate to="/dashboard" replace />
                )
              } 
            />
            <Route
              path="tab-b"
              element={
                user?.role === 'Admin' || user?.role === 'Leader' ? (
                  <DataTable dataType="B" title="Project Data (Tab B)" />
                ) : (
                  <Navigate to="/dashboard" replace />
                )
              }
            />
            <Route
              path="tab-c"
              element={
                <DataTable dataType="C" title="Employee Data (Tab C)" />
              }
            />
            <Route 
              path="task" 
              element={
                <TaskTab />
              } 
            />
            <Route 
              path="category" 
              element={
                user?.role === 'Admin' || user?.role === 'Leader' ? (
                  <CategoryTab />
                ) : (
                  <Navigate to="/dashboard" replace />
                )
              } 
            />
            <Route 
              path="team" 
              element={
                user?.role === 'Admin' ? (
                  <TeamManagement />
                ) : (
                  <Navigate to="/dashboard" replace />
                )
              } 
            />
            <Route 
              path="" 
              element={<Navigate to={availableTabs[0]?.path || '/dashboard/tab-c'} replace />} 
            />
          </Routes>
        </div>
      </Content>
    </Layout>
  );
};

export default Dashboard; 